import { ARRAY_MAX_LENGTH, HttpStatus, INPUT_TEXT_MAX_LENGTH } from '@/common/constants';
import yup from '@/plugins/yup';
import { useI18n } from 'vue-i18n';
import { projectPlanningModule } from '../store';
import { computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { ElLoading } from 'element-plus';
import { projectPlanningService } from '../services/planning.service';
import {
    showErrorNotificationFunction,
    showSuccessNotificationFunction,
} from '@/common/helpers';
import { AppearanceOptions, AppearanceType, DEFAULT_CUSTOM_COLOR, GrowthSimulation } from '../constants';
import { projectModule } from '@/features/project/store';
import {
    IActiveAppearanceProfile,
    IEndAppearanceProfile,
    IStartAppearanceProfile,
} from '../interfaces';

const directionRegex =
    /^\([+-]?\d*\.?\d+,[+-]?\d*\.?\d+,[+-]?\d*\.?\d+\)$|^[+-]?\d*\.?\d+,[+-]?\d*\.?\d+,[+-]?\d*\.?\d+$/;

const appearanceProfileSchema = yup.object({
    name: yup.string().max(INPUT_TEXT_MAX_LENGTH).required().trim(),
    type: yup
        .string()
        .oneOf([...Object.values(AppearanceType)])
        .required(),
    assignFileIds: yup
        .array()
        .of(yup.string().optional().optional().nullable())
        .max(ARRAY_MAX_LENGTH)
        .optional(),
    startAppearanceProfile: yup.object({
        colorType: yup
            .string()
            .oneOf([...Object.values(AppearanceOptions)])
            .required(),
        colorValue: yup.string().when('colorType', {
            is: AppearanceOptions.CUSTOM,
            then: yup
                .string()
                .transform((value) => (value === null ? undefined : value))
                .matches(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
                .required(),
        }),
        transparencyType: yup
            .string()
            .oneOf([...Object.values(AppearanceOptions)])
            .required(),
        transparencyValue: yup
            .number()
            .min(0)
            .max(100)
            .when('transparencyType', {
                is: AppearanceOptions.CUSTOM,
                then: yup
                    .number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .required(),
            })
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable(true),
    }),
    activeAppearanceProfile: yup.object({
        colorType: yup
            .string()
            .oneOf([...Object.values(AppearanceOptions)])
            .required(),
        colorValue: yup.string().when('colorType', {
            is: AppearanceOptions.CUSTOM,
            then: yup
                .string()
                .transform((value) => (value === null ? undefined : value))
                .matches(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
                .required(),
        }),
        transparencyType: yup
            .string()
            .oneOf([...Object.values(AppearanceOptions)])
            .required(),
        transparencyStartValue: yup
            .number()
            .min(0)
            .max(100)
            .when('transparencyType', {
                is: AppearanceOptions.CUSTOM,
                then: yup
                    .number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .required(),
            })
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable(true),
        transparencyInterpolation: yup.boolean(),
        transparencyEndValue: yup
            .number()
            .min(0)
            .max(100)
            .when('transparencyInterpolation', {
                is: true,
                then: yup
                    .number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .required(),
            })
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable(true),
    }),
    endAppearanceProfile: yup.object({
        colorType: yup
            .string()
            .oneOf([...Object.values(AppearanceOptions)])
            .required(),

        colorValue: yup.string().when('colorType', {
            is: AppearanceOptions.CUSTOM,
            then: yup
                .string()
                .transform((value) => (value === null ? undefined : value))
                .matches(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
                .required(),
        }),
        transparencyType: yup
            .string()
            .oneOf([...Object.values(AppearanceOptions)])
            .required(),
        transparencyValue: yup
            .number()
            .min(0)
            .max(100)
            .when('transparencyType', {
                is: AppearanceOptions.CUSTOM,
                then: yup
                    .number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .required(),
            })
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable(true),
    }),
    activeGrowthSimulation: yup.object({
        type: yup
            .string()
            .oneOf([...Object.values(GrowthSimulation)])
            .required(),
        direction: yup.string().when('type', {
            is: GrowthSimulation.CUSTOM,
            then: yup
                .string()
                .trim()
                .required()
                .test(
                    'is-valid-direction',
                    'planning.growthSimulation.form.messages.directionNotMatch',
                    (value) => {
                        if (!value) return true;
                        return directionRegex.test(value);
                    },
                ),
            otherwise: yup.string(),
        }),
        simulateAsRemoved: yup.boolean().optional().nullable(),
        isPercentageAdjusted: yup.boolean().optional().nullable(),
        isPausedInDayOff: yup.boolean().optional().nullable(),
    }),
});

export const innitStartAppearanceProfile = {
    colorType: AppearanceOptions.ORIGINAL,
    colorValue: '',
    transparencyType: AppearanceOptions.ORIGINAL,
    transparencyValue: null,
};

export const innitEndAppearanceProfile = {
    colorType: AppearanceOptions.ORIGINAL,
    colorValue: '',
    transparencyType: AppearanceOptions.ORIGINAL,
    transparencyValue: null,
};

export const innitActiveAppearanceProfile = {
    colorType: AppearanceOptions.CUSTOM,
    colorValue: DEFAULT_CUSTOM_COLOR[AppearanceType.INSTALL],
    transparencyType: AppearanceOptions.ORIGINAL,
    transparencyStartValue: null,
    transparencyInterpolation: false,
    transparencyEndValue: null,
};

export const innitActiveGrowthSimulation = {
    type: GrowthSimulation.NONE,
    direction: '',
    simulateAsRemoved: false,
    isPercentageAdjusted: false,
    isPausedInDayOff: false,
};

export const innitAppearanceProfile = {
    name: '',
    type: AppearanceType.INSTALL,
    assignFileIds: [],
    startAppearanceProfile: innitStartAppearanceProfile,
    activeAppearanceProfile: innitActiveAppearanceProfile,
    endAppearanceProfile: innitEndAppearanceProfile,
    activeGrowthSimulation: innitActiveGrowthSimulation,
};

export const setupAppearanceProfileForm = () => {
    const { t } = useI18n();
    const isCreate = computed(
        () => !projectPlanningModule.selectedAppearanceProfile?._id,
    );

    const {
        handleSubmit,
        errors,
        resetForm,
        validate,
        setErrors,
        validateField,
        setFieldValue,
    } = useForm({
        initialValues: innitAppearanceProfile,
        validationSchema: appearanceProfileSchema,
    });

    const onSubmit = async (submitSuccess?: () => void) => {
        await handleSubmit(async (values) => {
            let response;

            const loading = ElLoading.service({});

            const direction = values?.activeGrowthSimulation?.direction;
            const directionArray = direction?.replace(/[()]/g, '')?.split(',') || [];

            const payload = {
                name: values?.name || '',
                type: (values?.type || '') as AppearanceType,
                startAppearanceProfile:
                    values?.type !== AppearanceType.INSTALL &&
                    values?.type !== AppearanceType.TEMPORARY
                        ? ({
                              ...values?.startAppearanceProfile,
                          } as IStartAppearanceProfile)
                        : undefined,
                activeAppearanceProfile: {
                    ...values?.activeAppearanceProfile,
                } as IActiveAppearanceProfile,
                endAppearanceProfile:
                    values?.type !== AppearanceType.REMOVE &&
                    values?.type !== AppearanceType.TEMPORARY
                        ? ({ ...values?.endAppearanceProfile } as IEndAppearanceProfile)
                        : undefined,
                projectId: projectModule?.selectedProjectId || '',
                planningId: projectPlanningModule?.planning?._id,
                activeGrowthSimulation: {
                    type: (values?.activeGrowthSimulation?.type ||
                        GrowthSimulation.NONE) as GrowthSimulation,
                    simulateAsRemoved:
                        values?.activeGrowthSimulation?.simulateAsRemoved || false,
                    isPercentageAdjusted:
                        values?.activeGrowthSimulation?.isPercentageAdjusted || false,
                    isPausedInDayOff:
                        values?.activeGrowthSimulation?.isPausedInDayOff || false,
                    customDirection:
                        values?.activeGrowthSimulation?.type === GrowthSimulation.CUSTOM
                            ? {
                                  x: Number(directionArray?.[0] || 0),
                                  y: Number(directionArray?.[1] || 0),
                                  z: Number(directionArray?.[2] || 0),
                              }
                            : undefined,
                },
            };

            if (!isCreate.value) {
                const notAssignFileIds =
                    projectPlanningModule.selectedResource?.fileIds.filter(
                        (fileId) => !values?.assignFileIds?.includes(fileId),
                    );

                response = await projectPlanningService.updateAppearanceProfile(
                    projectPlanningModule.selectedAppearanceProfile?._id || '',
                    {
                        ...payload,
                        assignFileIds: values?.assignFileIds || [],
                        notAssignFileIds: notAssignFileIds || [],
                    },
                );
            } else {
                response = await projectPlanningService.createAppearanceProfile({
                    ...payload,
                });
            }
            loading.close();

            if (response?.success) {
                showSuccessNotificationFunction(
                    !isCreate.value
                        ? t('planning.appearanceProfile.message.updateSuccess')
                        : t('planning.appearanceProfile.message.createSuccess'),
                );
                projectPlanningModule.setIsShowAppearanceProfileDetailForm(false);
                const loading = ElLoading.service({
                    target: '.main-wrapper',
                });

                await Promise.all([
                    submitSuccess?.(),
                    projectPlanningModule.getAppearanceProfileList({
                        projectId: projectModule.selectedProjectId || '',
                        planningId: projectPlanningModule.planning?._id,
                    }),
                ]);

                loading.close();
                projectPlanningModule.setSelectedAppearanceProfile(null);
            } else {
                if (response.code === HttpStatus.ITEM_NOT_FOUND) {
                    projectPlanningModule.setIsShowAppearanceProfileDetailForm(false);
                    const loading = ElLoading.service({
                        target: '.main-wrapper',
                    });
                    await projectPlanningModule.getAppearanceProfileList({
                        projectId: projectModule?.selectedProjectId || '',
                        planningId: projectPlanningModule?.planning?._id,
                    });
                    loading.close();
                    projectPlanningModule.setSelectedAppearanceProfile(null);
                }
                showErrorNotificationFunction(response?.message);
            }
        })();
    };

    const { value: name } = useField('name');
    const { value: direction } = useField('direction');
    const { value: simulateAsRemove } = useField('simulateAsRemove');
    const { value: adjustForTaskPercentComplete } = useField(
        'adjustForTaskPercentComplete',
    );
    const { value: pauseDuringNonWorkingTime } = useField('pauseDuringNonWorkingTime');
    const { value: type } = useField('type');
    const { value: growthSimulation } = useField('growthSimulation');
    const { value: assignFileIds } = useField('assignFileIds');
    const { value: startAppearanceProfile } = useField('startAppearanceProfile');
    const { value: activeAppearanceProfile } = useField('activeAppearanceProfile');
    const { value: endAppearanceProfile } = useField('endAppearanceProfile');
    const { value: activeGrowthSimulation } = useField('activeGrowthSimulation');

    return {
        errors,
        onSubmit,
        resetForm,
        validate,
        setErrors,
        name,
        direction,
        simulateAsRemove,
        adjustForTaskPercentComplete,
        pauseDuringNonWorkingTime,
        type,
        growthSimulation,
        assignFileIds,
        startAppearanceProfile,
        activeAppearanceProfile,
        endAppearanceProfile,
        activeGrowthSimulation,
        validateField,
        setFieldValue,
    };
};
